import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dtpApi } from '../lib/dtpApi';
import { useDtpAuthStore } from '../store/useDtpAuthStore';

// Params an auto-import link may carry. Mirrors IMPORT_PARAM_KEYS on the backend
// (minus physicianId, which comes from the route path). `sig` is the HMAC.
const IMPORT_PARAM_KEYS = [
  'phone', 'firstName', 'lastName', 'dob', 'email',
  'street', 'street2', 'city', 'state', 'zip', 'country', 'exp', 'sig'
];

/**
 * Detects an HMAC-signed auto-import link (`?...&sig=...`) on a DTP page,
 * exchanges it for a patient session via the backend, and strips the params
 * from the URL so the signed link isn't left in the address bar / shared.
 * Runs once per mount, only when a `sig` is present.
 */
export function useDtpAutoImport(physicianId?: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const setAuth = useDtpAuthStore((s) => s.setAuth);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    const sig = searchParams.get('sig');
    if (!physicianId || !sig) return;
    ran.current = true;

    const params: Record<string, string> = {};
    for (const key of IMPORT_PARAM_KEYS) {
      const v = searchParams.get(key);
      if (v) params[key] = v;
    }

    setImporting(true);
    (async () => {
      try {
        const res = await dtpApi.autoImport(physicianId, params);
        if (res.success && res.data) {
          setAuth(res.data.token, res.data.patient, physicianId);
        } else {
          setError(res.error || 'This sign-in link is invalid or has expired.');
        }
      } catch (e: any) {
        setError(e?.response?.data?.error || 'This sign-in link is invalid or has expired.');
      } finally {
        // Strip the signed params either way — a consumed/failed link shouldn't
        // linger in the URL where it could be re-shared.
        const cleaned = new URLSearchParams(searchParams);
        IMPORT_PARAM_KEYS.forEach((k) => cleaned.delete(k));
        setSearchParams(cleaned, { replace: true });
        setImporting(false);
      }
    })();
  }, [physicianId, searchParams, setAuth, setSearchParams]);

  return { importing, error };
}
